













































































































































































































































import {
  computed,
  ComputedRef,
  defineComponent,
  onBeforeMount,
  reactive,
  SetupContext,
  watch,
} from '@vue/composition-api';
import EntityDetails from '@/components/common/EntityDetails.vue';
import { Category } from '@/models/entities/Category';
import { Location } from '@/models/entities/Location';
import { CategoryPreview } from '@/models/entities/Category';
import { DropdownFilter } from '@/models/filters/Filters';
import PaginatedAutocomplete from '@/components/utils/PaginatedAutocomplete.vue';
import ImageUpload from '@/components/utils/ImageUpload.vue';
import { ColorGroup } from '@/models/entities/ColorGroup';
import ProductCategoryOptionsSimpleTable from '@/components/entities/productCategoryOptions&Attributes/ProductCategoryOptionsSimpleTable.vue';
import ProductCategoryAttributesSimpleBlock from '@/components/entities/productCategoryOptions&Attributes/ProductCategoryAttributesSimpleBlock.vue';
import SizeChart from '@/components/entities/utils/SizeChart.vue';
import _ from 'lodash';
import { FontGroup } from '@/models/entities/FontGroup';
import { copyToClipboard } from '@/utils/clipboard';
import { dispatchAlert, AlertColor } from '@/utils/alerts';

interface Props {
  id: string;
}

interface State {
  entity: Category;
  loading: ComputedRef<boolean>;
  newLocation: Location;
  clearedSizeChart: boolean;
}

const Component = defineComponent({
  name: 'CategoryDetails',

  components: {
    SizeChart,
    EntityDetails,
    PaginatedAutocomplete,
    ImageUpload,
    ProductCategoryOptionsSimpleTable,
    ProductCategoryAttributesSimpleBlock,
  },

  props: {
    id: String,
  },

  setup(props: Props, { root: { $store, $router } }: SetupContext) {
    const category = new Category({});

    onBeforeMount(async () => {
      const res = await $store.dispatch(`categoriesModule/fetchItem`, props.id);
      if (!res) $router.back();
    });

    const state = reactive({
      entity: category,
      loading: computed(() => $store.getters['categoriesModule/loading']),
      newLocation: new Location({}),
      clearedSizeChart: false,
    }) as State;

    const categoryFilterDropdown = new DropdownFilter({
      label: 'Parent Category',
      itemsModuleName: 'categoriesModule',
      initialValue: category.parentCategory?.id,
    });
    const colorGroupFilterDropdown = new DropdownFilter({
      label: 'Color Group',
      itemsModuleName: 'colorGroupsModule',
      initialValue: category.colorGroup?.id,
    });
    const fontGroupFilterDropdown = new DropdownFilter({
      label: 'Font Group',
      itemsModuleName: 'fontGroupsModule',
      initialValue: category.fontGroup?.id,
    });

    watch(
      () => $store.getters['categoriesModule/selectedItem'],
      (newValue: Category) => {
        Object.assign(category, newValue);
        categoryFilterDropdown.initialValue = category.parentCategory?.id;
        colorGroupFilterDropdown.initialValue = category.colorGroup?.id;
        fontGroupFilterDropdown.initialValue = category.fontGroup?.id;
      },
      { immediate: false, deep: true },
    );

    const title = computed(() => {
      if (state.loading) return '';
      return `Edit Category ${$store.getters['categoriesModule/selectedItem']?.name}`;
    });

    const rules = {
      name: [(v: string) => !!v || 'Name is required'],
      number: [
        (v: number) => v == undefined || v >= 0 || 'Only positive numbers allowed',
      ],
    };

    function setNewCategory(newCategory: CategoryPreview) {
      state.entity.parentCategory = newCategory;
    }

    function setNewColorGroup(newColorGroup: ColorGroup) {
      state.entity.colorGroup = newColorGroup;
    }

    function setNewFontGroup(newFontGroup: FontGroup) {
      state.entity.fontGroup = newFontGroup;
    }

    function setNewPrice(newPrice: number, quantityBreakId: number) {
      const priceObj = state.entity.prices.find(
        price => price.quantityBreak.id === quantityBreakId,
      );
      if (priceObj !== undefined) priceObj.price = newPrice;
    }

    /** Return id of child subcategories (recursive) */
    function getChildIds(allItems: Category[], parentId: number): number[] {
      const children = allItems.filter(item => item.parentCategory?.id === parentId);
      const childrenIds = children.map(child => child.id);
      children.forEach(child => {
        childrenIds.push(...getChildIds(allItems, child.id));
      });
      return childrenIds;
    }

    /**
     * Filter items for parent category dropdown.
     * Remove category itself and its children from the list.
     * @param items
     */

    function parentCategoryFilter(items: Category[]): Category[] {
      const isSelfReference = (item: Category) => item.id === state.entity.id;
      const childIds = getChildIds(items, state.entity.id);
      const isChildCategory = (item: Category) => childIds.includes(item.id);
      const filtered = items.filter(
        item => !isSelfReference(item) && !isChildCategory(item),
      );
      return filtered;
    }

    const deleteConfirmation = {
      questionText: computed(
        () =>
          `${category.name} has ${category.subCategoriesAmount} ${
            category.subCategoriesAmount <= 1 ? 'sub-category' : 'sub-categories'
          } in it. Their parent category will be unset. Proceed?`,
      ),
      appearIf: computed(() => category.subCategoriesAmount > 0),
    };

    function getHiddenAttributes() {
      const hidden = [];
      const option = state.entity.productCategoryOptions.find(
        item => item.keyWord === 'extraTop',
      );
      if (!option || !option.value) {
        hidden.push('extraTopLength');
      }
      return hidden;
    }

    const updateSizeChartValue = _.debounce((val: any, rnum?: number, cnum?: number) => {
      if (state.entity.sizeChart.length < 1) {
        state.entity.sizeChart.push(['', '']);
        state.entity.sizeChart.push(['', '']);
      } else if (typeof val === 'string' && rnum !== undefined && cnum !== undefined) {
        state.entity.sizeChart[rnum][cnum] = val;
      } else {
        state.entity.sizeChart = val;
        if (val.length === 0) {
          state.clearedSizeChart = true;
        }
      }
    });

    async function copyCategoryUrl() {
      await copyToClipboard(state.entity.designerUrl)
        .then(() => dispatchAlert(AlertColor.SUCCESS, 'Copied!', $store.dispatch))
        .catch(() => dispatchAlert(AlertColor.ERROR, 'Failed to copy!', $store.dispatch));
    }

    return {
      state,
      title,
      rules,
      setNewCategory,
      setNewColorGroup,
      setNewFontGroup,
      setNewPrice,
      categoryFilterDropdown,
      parentCategoryFilter,
      colorGroupFilterDropdown,
      fontGroupFilterDropdown,
      deleteConfirmation,
      getHiddenAttributes,
      updateSizeChartValue,
      copyCategoryUrl,
    };
  },
});
export default Component;
